/*============================================================
   REACT DEPENDENCIES
============================================================*/
import { useState, useEffect } from "react";

/*============================================================
   REDUX DEPENDENCIES
============================================================*/
import { useDispatch, useSelector } from "react-redux";

import Grid from "@mui/material/Grid2";
import FormHelperText from "@mui/material/FormHelperText";
import Typography from "@mui/material/Typography";
import TextareaAutosize from "@mui/material/TextareaAutosize";

import GridSectionHeader from "../../widgets/GridSectionHeader";
import GridTextfield from "../../widgets/GridTextfield";

import {
  valudateTextfield2,
  validateCurrencyField3,
  removeCommaFromCurrency,
  convertLocaleString,
} from "../../../utils/fieldValidator.js";

import { updatePersonalAccident } from "../../../states/slcObjectives.js";

export default function PersonalAccident() {
  const personalAccident = useSelector(
    (state) => state.objectives.protection.personalAccident
  );

  const [paNeedsAmt, setPaNeedsAmt] = useState(personalAccident.paNeedsAmt);
  const [paExistingResource, setPaExistingResource] = useState(
    personalAccident.paExistingResource
  );
  const [paSurplusShortfall, setPaSurplysShortfall] = useState(
    personalAccident.paSurplusShortfall
  );

  const [deviationReason, setDeviationReason] = useState(
    personalAccident.deviationReason
  );
  const [deviationReasonError, setDeviationReasonError] = useState();

  const dispatch = useDispatch();

  //COMPONENT ID
  let id = "";

  /**==============================
   * EFFECTS
   ==============================*/
  useEffect(calcSurplusOrShortfall, [paExistingResource, paNeedsAmt]);

  /**==============================
   * FUNCTIONS
   ==============================*/
  function calcSurplusOrShortfall() {
    let surplusShortfall =
      Number(removeCommaFromCurrency(paExistingResource)) -
      Number(removeCommaFromCurrency(paNeedsAmt));

    surplusShortfall = convertLocaleString(surplusShortfall, "en-US", 2);

    updateRedux("paSurplusShortfall", surplusShortfall, updatePersonalAccident);
    setPaSurplysShortfall(surplusShortfall);
  }

  function updateRedux(fieldName, fieldValue, reducer) {
    const action = {
      type: fieldName,
      payload: fieldValue,
    };
    dispatch(reducer(action));
  }

  function handleChange(e, object) {
    let input_data = {
      event: e,
      fieldName: "",
      fieldVariable: "",
      isMandatory: false,
    };

    //TEXTFIELDS
    id = e.target.id;
    validateFields(e, id, input_data);
  }

  function validateFields(e, id, input_data) {
    if (id === "paNeedsAmt") {
      input_data.fieldName = "Personal Accident Death Needs";
      input_data.fieldVariable = paNeedsAmt;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setPaNeedsAmt(result.value);
        updateRedux("paNeedsAmt", result.value, updatePersonalAccident);
      }
    }

    if (id === "paExistingResource") {
      input_data.fieldName = "Personal Accident Existing Eesource";
      input_data.fieldVariable = paExistingResource;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setPaExistingResource(result.value);
        updateRedux("paExistingResource", result.value, updatePersonalAccident);
      }
    }

    if (id === "deviationReason") {
      input_data.fieldName = "Deviation reason";
      input_data.fieldLength = 1000;
      input_data.fieldVariable = deviationReason;
      const result = valudateTextfield2(input_data);
      if (result.errorMessage === "") {
        setDeviationReason(result.value);
        updateRedux("deviationReason", result.value, updatePersonalAccident);
      }

      setDeviationReasonError(result.errorMessage);
    }
  }

  return (
    <>
      <Grid container spacing={1} size={12}>
        <GridSectionHeader
          sectionName="Personal Accident"
          sx={{
            p: 1,
            pl: 1.8,
            mt: 0,
            mb: 1,
            fontSize: 18,
            fontWeight: "bold",
            color: "#FFFFFF", //WHITE
            // color: "#000000", //BLACK
            // backgroundColor: "#d9d9d9",
            backgroundColor: "#10B981",
          }}
        />
      </Grid>

      {/*==============================
      PERSONAL ACCIDENT DEATH NEEDS
      ==============================*/}
      <Grid size={9}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.0,
          }}
        >
          (A) Personal Accidental Death Needs
        </Typography>
      </Grid>

      <GridTextfield
        id="paNeedsAmt"
        key="paNeedsAmt"
        columnSize="3"
        isCurrency={true}
        currState={paNeedsAmt}
        onChange={handleChange}
      />

      {/*==============================
        EXISTING RESOURCES IF ANY
        ==============================*/}
      <Grid size={9}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.0,
          }}
        >
          (B) Existing Resources (if any)
        </Typography>
      </Grid>

      <GridTextfield
        id="paExistingResource"
        key="paExistingResource"
        columnSize="3"
        isCurrency={true}
        currState={paExistingResource}
        onChange={handleChange}
      />

      {/*==============================
        SURPLUS / SHORTFALL
        ==============================*/}
      <Grid size={9}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.0,
          }}
        >
          (B - A) = Surplus / (Shortfall)
        </Typography>
      </Grid>

      <GridTextfield
        id="paSurplusShortfall"
        key="paSurplusShortfall"
        columnSize="3"
        isCurrency={true}
        currState={paSurplusShortfall}
        onChange={handleChange}
        disabled={true}
      />

      {/*==============================
        REASON FOR DEVIATION
      ==============================*/}
      <Grid size={12}>
        <Typography variant="body1">
          <b>Reason(s) for Deviations:</b>
        </Typography>

        <TextareaAutosize
          id="deviationReason"
          placeholder="Type here..."
          style={{ width: "100%" }}
          minRows={5}
          onChange={handleChange}
          defaultValue={deviationReason}
        />
        {deviationReasonError && (
          <FormHelperText error={deviationReasonError}>
            {deviationReasonError}
          </FormHelperText>
        )}
      </Grid>
    </>
  );
}
